import { AsyncJob } from './../interfaces/async-job-data';
import { Injectable} from '@angular/core';
import { Observable, Subscription, shareReplay, tap } from 'rxjs';
import { AdminService} from './admin.service';
import { MiVBProvisioningService } from './mivb-provisioning.service';
import { AccountsService } from './accounts.service';
import { MiCollabService } from './micollab.service';
import { Util } from '../utils/Util';
import { environment } from 'src/environments/environment';
import { Config, HttpMethod } from '@mitel/cloudlink-sdk';
import { callRerouting1stAlternative, callRerouting2ndAlternative, callReroutingDay, callReroutingNight1, callReroutingNight2 } from '../constants/gdm-user-configuration-cavh';

export interface CLDSettings {
  miCollabNetworkElement: any;
}

@Injectable({
	providedIn: 'root'
})
export class MiVBAccountSettingsService {
	private tunnelService: any;
	private subscriptionInitialized: boolean = false;
	subscription: Subscription = new Subscription();

	private _networkElements: any[] | undefined;
	private _siteId: string | undefined;
	private _miCollabIPFQDN: string | undefined;
	private _miCollabNetworkElements: any[] | undefined;
	private _isServerMSVI: boolean | undefined;
	private _doesAccountHaveMiVBNetworkElementObservable: Observable<boolean>;
	private _dynamicDimensions: any[] | undefined;


	constructor(
		private accountService: AccountsService,
		private adminService: AdminService,
		private miVBProvisioning: MiVBProvisioningService,
		private miCollabService: MiCollabService
	) {
		this.tunnelService = {
			rootUrl: environment.tunnelUrl,
			_http: <HttpMethod> Config.httpService,

			async getGDMUserConfigurationCAVH(componentId?: string): Promise<any> {
				let url = `${this.rootUrl}/api/v1/proxy/gdc/pbx/1/gvd/0/198`;
				let headers = { 'x-mitel-tunnel-service': 'clgwapi' };
				componentId ?
				 headers['x-mitel-tunnel-component-id'] = componentId :
				 headers['x-mitel-tunnel-component'] = 'mivoice-business';
				return this._http.get({ url, headers}).then((response) => response.body);
			},
		}
	}

    destroySubscriptions() {
		if(this.subscription) {
			this.subscription.unsubscribe();
			this.subscriptionInitialized = false;
		}
	}

    /*
	To be called when the current account changes. 
	As more account-level mivb settings become important, they should be reset here.
	*/
	resetMiVBAccountLevelSettings(){
		this._networkElements = undefined;
		this._siteId = undefined;
		this._miCollabIPFQDN = undefined;
		this._miCollabNetworkElements = undefined;
		this._isServerMSVI = undefined;
		this._doesAccountHaveMiVBNetworkElementObservable = undefined;
		this._dynamicDimensions = undefined;
	}

	/*
	Mivb components will only initialize this subscription if necessary.
	A change in the current account will trigger resetting variables storing mivb-account-level settings.
	An undefined value for an mivb-account-level setting will result in an api call to determine the value - but only if necessary.
	At this point, we don't want to retrieve mivb-account-level settings until there is a specific need for it.
	*/ 
	initializeMiVBAccountSubscription(){
		if(!this.subscriptionInitialized){
			this.subscriptionInitialized = true;
			this.subscription.add(this.accountService.currentAccountChanged.subscribe(()=> {
				this.resetMiVBAccountLevelSettings();
			}));
		}
	}

	/*
	 If you don't have the network elements, make the api call to get them.
	 At this time, if you already have them, no need to make the call again.
	 The Accounts app currently does not allow for changing the list of Network Elements
	 for an account.
	 If the admin changes the network elements on the pbx (very rare), they will need to refresh the app.
	 If that becomes cumbersome for them, we can revisit this logic. 
	*/
	async getMiVBNetworkElements(): Promise<any> {
		if(!this._networkElements){
      const accountId = this.accountService.getCurrentAccount()?.accountId;
			return this.miVBProvisioning.getMiVBNetworkElements(accountId)
			.then((response) => {
				this._networkElements = ("networkElements" in response) ? response.networkElements : undefined;
				console.log("Newly fetched network elements (via new route): ", this._networkElements);
				return this._networkElements;
			}).catch(err => {
				console.log(err);
				console.warn("Failed to get list of mivb network elements (via new route)");
				this._networkElements = undefined;
				return this._networkElements;
			});
		}
		else{
			console.log("network elements (via new route): ", this._networkElements);
			return Promise.resolve(this._networkElements);
		}
	}

	/* 
		Many components/services might need to know if there's an mivb in the network elements
		And they might all be asking around the same time
		And they might all be waiting before they can continue
		The network elements call can be slow, and the result is not expected to change often
		(only when pbxs are removed/added).
		Ideally, we only make this call once, and share the result
		Ticket raised for further reworking: CLSD-4218
	*/
	get isMiVBInNetworkElementsObservable(): Observable<boolean>{

		if(this._doesAccountHaveMiVBNetworkElementObservable === undefined){
			this._doesAccountHaveMiVBNetworkElementObservable = Observable.create(async (observer) => {
				let mivbPbxExists = await this.isMiVBInNetworkElements();
				observer.next( mivbPbxExists );
			}).
			pipe(
				tap(() => {console.log('isMiVBInNetworkElementsObservable() TAP method executed')}),
				shareReplay(1)
			);
		}
		return this._doesAccountHaveMiVBNetworkElementObservable;
	}

	async isMiVBInNetworkElements(): Promise<boolean>{
		if(!this._networkElements){
			await this.getMiVBNetworkElements();
		}
		return Util.doesAccountHaveMiVoiceBusinessInNetworkElements(this._networkElements);
	}
  
  async getCLDSettings(): Promise<CLDSettings | undefined> {
    if (!this._networkElements) {
      await this.getMiVBNetworkElements();
    }
    let cldSettings = undefined;
    let doesAccountSupportMultiplePBX = Util.doesAccountSupportMultiplePBX(this._networkElements);
    if (doesAccountSupportMultiplePBX) {
      cldSettings = {
        miCollabNetworkElement: Util.getMiCollabFromNetworkElements(this._networkElements)
      }
    }
    return cldSettings;
  }

    /*
	 If you don't have the siteId, make the api call to get it.
	 Since siteId is not expected to change for an account, if you already know siteId no need to make call again.
	*/

  async getSiteId(accountId: string): Promise<any> {
    if (!this._siteId) {
      if (accountId) {
        return this.adminService.getSiteIdForAccount(accountId)
          .then((response) => {
            this._siteId = response
            console.log("Newly fetched MiVB siteId: ", this._siteId);
            return this._siteId;
          }).catch(err => {
            console.log(err);
            console.warn("Failed to get site Id");
            this._siteId = undefined;
            return this._siteId;
          });
      }
      else {
        console.warn("Failed to get siteId: missing accountId");
        this._siteId = undefined;
        return Promise.resolve(this._siteId);
      }
    }
    else {
      return Promise.resolve(this._siteId);
    }
  }

  async getMiCollabIPFQDN(accountId: string): Promise<any> {
    if (!this._miCollabIPFQDN) {
      try {        
        let cldSettings = await this.getCLDSettings();
        if (cldSettings) {
          if (cldSettings.miCollabNetworkElement && 'fqdnOrIpAddress' in cldSettings.miCollabNetworkElement) {
            this._miCollabIPFQDN = cldSettings.miCollabNetworkElement.fqdnOrIpAddress as string;
            console.log("MiCollab fqdnOrIpAddress: ", this._miCollabIPFQDN);
            return Promise.resolve(this._miCollabIPFQDN);
          } else {
            this._miCollabIPFQDN = 'NONE';
            console.log("MiCollab fqdnOrIpAddress: ", this._miCollabIPFQDN);
            return Promise.resolve(this._miCollabIPFQDN);
          }
        } else {
          let siteId = await this.getSiteId(accountId);
          if (siteId) {
            let config = await this.miCollabService.getMiCollabConfiguration(accountId, siteId);
            if (config && config === 'not found') {
              this._miCollabIPFQDN = 'NONE';
              console.log("MiCollab IP/FQDN: ", this._miCollabIPFQDN);
              return Promise.resolve(this._miCollabIPFQDN);
            }
            else if (config && 'fqdn' in config) {
              this._miCollabIPFQDN = config.fqdn;
              console.log("MiCollab IP/FQDN: ", this._miCollabIPFQDN);
              return Promise.resolve(this._miCollabIPFQDN);
            }
            else {
              console.warn('Failed to determine micollab presence');
              return Promise.reject('Error getting MiCollab details: failed to determine micollab presence');
            }
          }
          else {
            console.warn('could not find siteId');
            return Promise.reject('Error getting MiCollab details: could not find siteId');
          }
        }
      } catch (error) {
        console.warn('Failed to get MiCollab IP/FQDN');
        return Promise.reject('Error getting MiCollab details: failed to get MiCollab IP/FQDN');
      }
    }
    else {
      console.log("MiCollab IP/FQDN: ", this._miCollabIPFQDN);
      return Promise.resolve(this._miCollabIPFQDN);
    }
  }

  async getMiCollabVoicemailAccessNumber(accountId: string, elementId: string): Promise<any> {
    if (!this._miCollabNetworkElements) {
      try {
        let cldSettings = await this.getCLDSettings();
        if (cldSettings) {
          if (cldSettings.miCollabNetworkElement && 'componentId' in cldSettings.miCollabNetworkElement) {
            let componentId = cldSettings.miCollabNetworkElement['componentId'] as any;
            let miCollabNetworkElements = await this.miCollabService.getMiCollabNetworkElementsForCLD(componentId);
            if (miCollabNetworkElements && miCollabNetworkElements.length > 0) {
              this._miCollabNetworkElements = miCollabNetworkElements;
              console.log('Network Elements from MiCollab - CLD: ', this._miCollabNetworkElements);
            }
            else {
              console.warn('Failed to determine network elements from MiCollab - CLD');
              return Promise.reject(null);
            }
          } else {
            console.warn("Failed to determine network elements from MiCollab");
            return Promise.reject(null);
          }
        } else {
          let siteId = await this.getSiteId(accountId);
          if (siteId) {
            let miCollabNetworkElements = await this.miCollabService.getMiCollabNetworkElements(accountId, siteId);
            if (miCollabNetworkElements && miCollabNetworkElements.length > 0) {
              this._miCollabNetworkElements = miCollabNetworkElements;
              console.log("Network Elements from MiCollab: ", this._miCollabNetworkElements);
            }
            else {
              console.warn("Failed to determine network elements from MiCollab");
              return Promise.reject(null);
            }
          }
          else {
            console.warn('Failed to determine network elements from MiCollab - could not find siteId');
            return Promise.reject(null);
          }
        }
      } catch (error) {
        console.warn('Failed to determine voicemail access number');
        return Promise.reject(null);
      }
    }
		
		if(this._miCollabNetworkElements){
			console.log("Network Elements from MiCollab: ", this._miCollabNetworkElements);
			let matchingElement = this._miCollabNetworkElements.find(el => el.id && el.id === elementId);
			let accessNumber = "";
			if(matchingElement){
				if(('voiceMailServerType' in matchingElement) && matchingElement.voiceMailServerType === "EMEM"){
					accessNumber = matchingElement.huntGroupNumber? matchingElement.huntGroupNumber : "";
				}
				else if(('destinationDirectoryNumber' in matchingElement) || ('huntGroupNumber' in matchingElement)){
					accessNumber = matchingElement.destinationDirectoryNumber? matchingElement.destinationDirectoryNumber : matchingElement.huntGroupNumber? matchingElement.huntGroupNumber : "";
				}
				else{
					return Promise.reject(null);
				}
				return Promise.resolve(accessNumber);
			}
			else{
				return Promise.reject(null);
			}
		}
	}

	/* 
	- From CLSD-3265:
	- MSVI = Mitel Solution Virtual Instance which includes in one OVA deployment, MiVB, MBG & Micollab.
	- Eth 0 - Contains two Lan IPs (1st for MiVB & 2nd for shared MBG/Micollab)
	- No support for Nupoint - EMEM only and 
	- No Support for AWV
	- The /portal will not be supported in this config.
	*/
	async isMSVIPresent(): Promise<boolean> {
    if (this._isServerMSVI === undefined) {
      let componentId = undefined;
      let cldSettings = await this.getCLDSettings();
      if (cldSettings) {
        if (cldSettings.miCollabNetworkElement && 'componentId' in cldSettings.miCollabNetworkElement) {
          componentId = cldSettings.miCollabNetworkElement['componentId'] as any;
        }
      }

      if (!cldSettings || componentId) {
        try {
          let isServerMSVI = await this.miCollabService.getNetworkServicesById("mivbsvi", componentId);
          this._isServerMSVI = isServerMSVI ? true : false;
          console.log("Mitel Solution Virtual Instance: ", JSON.stringify(isServerMSVI));
        } catch (error) {
          if (error && error.statusCode && (error.statusCode === 404)) {
            this._isServerMSVI = false; //don't need to reject when MSVI does not exist
            console.log("Mitel Solution Virtual Instance: false");
          }
          else {
            console.warn('Failed to determine MSVI status from MiCollab');
            return Promise.reject(null);
          }
        }
      } else {
        this._isServerMSVI = false; //don't need to reject when MSVI does not exist
        console.log("Mitel Solution Virtual Instance: false");
      }
    }

    console.log("MSVI presence: ", this._isServerMSVI);
    return Promise.resolve(this._isServerMSVI);
	}

  async isMiCollabPresent(accountId: string): Promise<boolean> {
    let cldSettings = await this.getCLDSettings();
    if (cldSettings) {
      return Boolean(cldSettings.miCollabNetworkElement);
    } else {
      let siteId = await this.getSiteId(accountId);
      return await this.miCollabService.isMiCollabConfigured(accountId, siteId);
    }
  }

	async getAllDynamicDimensions(): Promise<any> {
		if (!this._dynamicDimensions) {
			return await this.getMiVBNetworkElements()
			.then((networkElements) => networkElements.filter((element) => element.type === '3300 ICP'))
			.then((networkElements) => {
				let promises = [];
				let localNetworkElements = networkElements.filter((element) => element.local === true);
				if (localNetworkElements.length > 0) {
					promises.push(this.getDynamicDimensions());
				} else {
					promises = networkElements.map((element) => this.getDynamicDimensions(element.componentId));
				}
				return Promise.all(promises).then((results) => {
					this._dynamicDimensions = results;
					return this._dynamicDimensions;
				});
			}).catch((error) => {
				console.warn('Failed to get dynamic dimensions', error);
				this._dynamicDimensions = undefined;
				return this._dynamicDimensions;
			});
		} else {
			return Promise.resolve(this._dynamicDimensions);
		}
	}

	async getDynamicDimensions(componentId?: string) {
			return await this.tunnelService.getGDMUserConfigurationCAVH(componentId)
			.then((response) => response.view_desc.field_descs)
			.then((fields: any[]) => {
				return {
					componentId,
					callHandlingDay: this.determineDimensions(fields, callReroutingDay),
					callHandlingNight: this.determineDimensions(fields, callReroutingNight1),
					callHandlingAltNight: this.determineDimensions(fields, callReroutingNight2),
					callRerouting1stAlternative: this.determineDimensions(fields, callRerouting1stAlternative),
					callRerouting2ndAlternative: this.determineDimensions(fields, callRerouting2ndAlternative)
				}
			});
	}

	determineDimensions(fields: any[], targetField: any) {
		let fieldData;

		// In general, the field id is the same as the index in the array
		if (fields.length >= targetField.id) {
			fieldData = fields[targetField.id];
		}

		// If the field is not at the expected index, search for it
		if (fieldData?.name?.toLowerCase() != targetField.name.toLowerCase()) {
			fieldData = fields.find((field) => field.id === targetField.id);
		}

		// If the field is found, get the max value
		if (fieldData && fieldData.enum && fieldData.enum.length > 0) {
			let length = fieldData.enum.length;
			let max = fieldData.enum[length - 1].value;
			return max;

		// If the field is not found, return the default max value
		} else {
			return targetField.defaultMax; // Later, we may consider throwing an error or handling this case differently
		}
	}

  /**
  Retrieve all asynchronous jobs for an account
  */
  async getAllAsyncJobs(skipToken?: string): Promise<AsyncJob[]> {    
    let top = 100;
    return new Promise<AsyncJob[]>(async (resolve, reject) => {
      let asyncJobs: AsyncJob[];
      try {
        const asyncJobCollection = await this.miVBProvisioning.getAsyncJobs(top, skipToken);
        if (asyncJobCollection && asyncJobCollection.count > 0 && asyncJobCollection._embedded && asyncJobCollection._embedded.items) {
          asyncJobs = asyncJobCollection._embedded.items;
          if (asyncJobCollection._links && asyncJobCollection._links.next && asyncJobCollection.count) {
            const moreAsyncJobs = await this.getAllAsyncJobs(asyncJobCollection._links.next);
            if (moreAsyncJobs) {
              asyncJobs = asyncJobs.concat(moreAsyncJobs);
            }
          }
        }
        resolve(asyncJobs);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
  Retrieve an asynchronous job by id for an account
  */
  async getAsyncJobById(asyncJobId: string, query?: string): Promise<AsyncJob> {
    if (asyncJobId) {
      return new Promise<AsyncJob>(async (resolve, reject) => {
        try {
          resolve(this.miVBProvisioning.getAsyncJobById(asyncJobId, query));
        } catch (err) {
          reject(err);
        }
      });
    }
    else {
      console.warn('Failed to get asynchronous job by id for an account: missing accountId or asyncJobId');
      return Promise.resolve(undefined);
    }
  }
		
  /**
  Update an asynchronous job by id for an account
  */
  async putAsyncJobById(asyncJobId: string, body?: any): Promise<AsyncJob> {
    if (asyncJobId) {
      return new Promise<AsyncJob>(async (resolve, reject) => {
        try {
          resolve(this.miVBProvisioning.putAsyncJobById(asyncJobId, body));
        } catch (err) {
          reject(err);
        }
      });
    }
    else {
      console.warn('Failed to update asynchronous job by id for an account: missing asyncJobId');
      return Promise.resolve(undefined);
    }
  }

  async getLocations() {
    const networkElements = await this.getMiVBNetworkElements();
    
    if (networkElements?.length > 0) {
      let homeNetworkElement = undefined;
      const pbxs = networkElements.filter(element => { return element.type && element.type === '3300 ICP' });
      if (pbxs?.length > 0) {
        const localPBXs = pbxs.filter((pbx) => { return pbx.local === true });
        if (localPBXs?.length > 0) {
          if (localPBXs.length == 1) { 
            homeNetworkElement = localPBXs[0];
          }
        }
        else {
          //if there are no 3300 ICP with local=true, then there could be multiple pbxs that can act as the home pbx
          homeNetworkElement = pbxs[0];
        }
      }

      // networkElementId is arbitrary for this call
      return this.miVBProvisioning.getLocations(homeNetworkElement?.id).then(locations => {
        //sort alphabetically
        locations?.sort(function (a: any, b: any) {
          return a?.name?.localeCompare(b?.name)
        });

        return locations ?? [];
      })
    }

    return [];
  }
}